import BassEditor, { KeyLearning } from '../bass/BassEditor';
import { generatePattern, parseSessionKey } from '../utils/knockEngine';
import React, { useRef, useState } from 'react';
import { usePreview } from '../contexts/PreviewContext';
import { usePlayer } from '../contexts/PlayerContext';
import { useReflect } from '../contexts/ReflectContext';
import { usePlaybackCoordinator } from '../contexts/PlaybackCoordinatorContext';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { 
  Play, 
  Square, 
  X, 
  Volume2, 
  VolumeX,
  ChevronUp,
  ChevronDown,
  Trash2,
  Music,
  ShoppingBag,
  Check,
  Zap,
  RefreshCw,
  Shuffle,
  Lock,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { surfaceForGroup, textForGroup, dotForGroup, GROUP_HEX } from '../utils/groupColors';
import { coachSteps } from '../utils/coachSteps';
import { preparePreviewHandoffTracks } from '../utils/previewHandoff';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';

let bassAuditionContext;
const getBassContext = () => { if (!bassAuditionContext || bassAuditionContext.state === 'closed') bassAuditionContext = new (window.AudioContext || window.webkitAudioContext)(); return bassAuditionContext; };
const FEEL_OPTIONS = [
  { value: 'basic', label: 'Basic' },
  { value: 'bouncy', label: 'Bouncy' },
  { value: 'sparse', label: 'Sparse' },
  { value: 'busy', label: 'Busy' }
];

const PreviewPanel = () => {
  // Guards the handoff. Sending opens a new tab and hands the whole preview
  // over, and the button sat next to the track controls, so a mis-click used to
  // be unrecoverable.
  const [confirmSend, setConfirmSend] = React.useState(false);
  const [bassEditorOpen, setBassEditorOpen] = useState(false);
  const {
    isOpen,
    setIsOpen,
    tracks,
    isPlaying,
    barLength,
    setBarLength,
    sessionBpm,
    togglePlayback,
    removeFromPreview,
    setTrackVolume,
    toggleTrackMute,
    clearPreview,
    restorePreview,
    restoreTrack,
    MAX_TRACKS,
    // Crate
    addKnockEngineToCrate,
    // Knock Engine
    knockEngineActive,
    knockEngineLoading,
    knockEngineData,
    knockEngineFeel,
    knockEngineVolume,
    knockEngineMuted,
    knockEngineKickFollow,
    detectedKickPositions,
    knockEngineAvailable,
    activateKnockEngine,
    changeKnockEngineFeel,
    updateKnockEngineVolume,
    toggleKnockEngineMute,
    toggleKnockEngineKickFollow,
    renderKnockEngineToBlob, updateKnockBass, bassLoadError,
    removeKnockEngine
  } = usePreview();
  
  const { currentLoop, isPlaying: cardIsPlaying, stop: stopCard } = usePlayer();
  const { activeOwner } = usePlaybackCoordinator();
  
  // Get reflect session ID if user came through Reflect flow
  let reflectSessionCode = null;
  try {
    const reflectContext = useReflect();
    reflectSessionCode = reflectContext?.sessionCode || null;
  } catch {
    reflectSessionCode = null;
  }
  
  const panelRef = useRef(null);
  const sendingToArranger = useRef(false);

  // A short pulse on the dock bar whenever a loop is added, so the student sees
  // it land without the panel opening over the library.
  const [justAdded, setJustAdded] = React.useState(false);
  const previousCount = useRef(tracks.length);
  React.useEffect(() => {
    if (tracks.length > previousCount.current) {
      setJustAdded(true);
      const timer = setTimeout(() => setJustAdded(false), 900);
      previousCount.current = tracks.length;
      return () => clearTimeout(timer);
    }
    previousCount.current = tracks.length;
    return undefined;
  }, [tracks.length]);
  
  // Derive dock display state
  const isCardOwner = activeOwner === 'card' && currentLoop && cardIsPlaying;
  const hasPreviewContent = tracks.length > 0;
  const isAudioActive = isPlaying || isCardOwner;
  const coach = coachSteps(tracks, knockEngineActive);

  // The app-shell dock cannot receive page props. These small events let the
  // library's coach and its empty slots use the exact existing hand-off paths.
  React.useEffect(() => {
    const selectGroup = (event) => window.dispatchEvent(new CustomEvent('sp-select-group', { detail: event.detail }));
    const send = () => setConfirmSend(true);
    window.addEventListener('sp-dock-select-group', selectGroup);
    window.addEventListener('sp-send-arranger', send);
    return () => { window.removeEventListener('sp-dock-select-group', selectGroup); window.removeEventListener('sp-send-arranger', send); };
  }, []);
  
  // Handler for "Arrange in Beat Arranger" — cross-app navigation via URL param
  const handleArrangeInBeatArranger = async () => {
    if (tracks.length === 0 || sendingToArranger.current) return;
    
    // Get session key from melody tracks
    const melodyTrack = tracks.find(t => t.instrumentGroup === 'Melodies' && t.musicalKey);
    const sessionKey = melodyTrack?.musicalKey || null;
    
    // Reserve the tab during the click, preserving popup permission while audio metadata loads.
    const opened = window.open('about:blank', '_blank');
    if (!opened) { toast.error('Your browser blocked the new tab. Allow pop-ups for Studio Playground, then try again.'); return; }
    opened.opener = null;
    sendingToArranger.current = true;
    const preparing = toast.loading('Preparing full loops for Beat Arranger…');
    try {
      const trackPayload = await preparePreviewHandoffTracks(tracks, sessionBpm);

      // Include Knock Engine bassline if active
      let knockPayload = null;
      if (knockEngineActive && knockEngineData) {
        knockPayload = {
          active: true,
          pattern: knockEngineData.pattern,
          feel: knockEngineData.feel,
          sound_type: knockEngineData.sound_name || 'Sub Sine',
          sample_id: knockEngineData.sample_id || null,
          lock_to_key: knockEngineData.lock_to_key !== false,
          osc_type: knockEngineData.osc_type,
          filter_freq: knockEngineData.filter_freq,
          detune: knockEngineData.detune,
          volume: knockEngineVolume,
          root_note: knockEngineData.root_note,
          scale_type: knockEngineData.scale_type,
          genre: knockEngineData.genre,
          key: `${knockEngineData.root_note} ${knockEngineData.scale_type}`,
          bpm: sessionBpm,
          bars: knockEngineData.bars || 4,
        };
      }
    
      const payload = {
        tracks: trackPayload,
        sessionBpm: sessionBpm,
        sessionKey: sessionKey,
        knockEngine: knockPayload,
        source: 'sp2',
        timestamp: Date.now(),
      };
    
      const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
      // Hardcoded to the old Emergent domain, which is dead. Env-driven now so
      // staging and production can point at different Beat Arranger deployments.
      const arrangerUrl = process.env.REACT_APP_BEAT_ARRANGER_URL
        || 'https://ltbb-beat-arranger.pages.dev';
      const handoffUrl = `${arrangerUrl}?ltbb_import=${encoded}`;

      opened.location.href = handoffUrl;
      if (isPlaying) togglePlayback();
      opened.focus();
    } catch (error) {
      opened.close();
      toast.error(error.message || 'The selected audio could not be loaded. Try sending again.');
    } finally {
      sendingToArranger.current = false;
      toast.dismiss(preparing);
    }
  };
  
  // Clearing and removing both offer an undo for a few seconds instead of a confirm
  // dialog, so the common case stays one tap and a mis-tap costs nothing.
  const handleClear = () => {
    const snapshot = clearPreview();
    const count = snapshot.tracks.length;
    toast(`Cleared ${count} loop${count === 1 ? '' : 's'}`, {
      duration: 8000,
      action: { label: 'Undo', onClick: () => restorePreview(snapshot) },
    });
  };

  const handleRemoveTrack = (track, index) => {
    removeFromPreview(track.id);
    toast(`Removed ${track.title}`, {
      duration: 8000,
      action: { label: 'Undo', onClick: () => restoreTrack(track, index) },
    });
  };

  // Build dock label
  let dockLabel, dockSub;
  if (isCardOwner) {
    dockLabel = `Now Playing: ${currentLoop.title}`;
    dockSub = `${currentLoop.bpm} BPM`;
  } else if (isPlaying) {
    dockLabel = `${tracks.length}/${MAX_TRACKS} loops \u00B7 Playing`;
    dockSub = sessionBpm ? `${sessionBpm} BPM` : '';
  } else if (hasPreviewContent) {
    dockLabel = `${tracks.length}/${MAX_TRACKS} loops ready`;
    dockSub = sessionBpm ? `${sessionBpm} BPM` : '';
  } else {
    dockLabel = '';
    dockSub = '';
  }
  
  // Handle dock info click — navigate to active controller
  const handleDockInfoClick = () => {
    if (isCardOwner) {
      // Stop card playback (equivalent to NowPlayingBar close)
      stopCard();
    } else {
      // Toggle expanded panel
      setIsOpen(!isOpen);
    }
  };
  
  // Don't render if nothing to show
  if (!hasPreviewContent && !isCardOwner) return null;
  
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40" ref={panelRef} data-testid="preview-dock">
      {/* Expanded Panel — opens upward from dock */}
      {hasPreviewContent && (
        <div
          className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
            isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          }`}
        >
          <div className="overflow-hidden min-h-0">
            <div className="bg-card/95 backdrop-blur-xl border-t border-x border-white/10 rounded-t-2xl max-w-3xl mx-auto shadow-2xl">
              {/* Expanded Header — bar length + session info */}
              <div className="flex items-center justify-between px-4 pt-4 pb-2">
                <div className="flex items-center gap-3">
                  <div className="flex bg-muted/50 rounded-full p-1">
                    <button
                      onClick={() => setBarLength(4)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                        barLength === 4 
                          ? 'bg-primary text-black' 
                          : 'text-muted-foreground hover:text-white'
                      }`}
                    >
                      4 bars
                    </button>
                    <button
                      onClick={() => setBarLength(8)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                        barLength === 8 
                          ? 'bg-primary text-black' 
                          : 'text-muted-foreground hover:text-white'
                      }`}
                    >
                      8 bars
                    </button>
                  </div>
                  {sessionBpm && (
                    <div className="text-sm text-muted-foreground">
                      Session: <span className="text-primary font-semibold">{sessionBpm} BPM</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="overflow-y-auto px-4 pb-4 space-y-3" style={bassEditorOpen && knockEngineData ? { height: '70vh', maxHeight: '70vh', padding: 0 } : { maxHeight: '50vh' }}>
                {bassEditorOpen && knockEngineData ? (<div className="sp-bass-editor-host"><BassEditor
                  value={{ pattern: knockEngineData.pattern, bars: knockEngineData.bars || 4, sampleId: knockEngineData.sample_id, soundType: knockEngineData.sound_name || 'Sub Sine', oscType: knockEngineData.osc_type, filterFreq: knockEngineData.filter_freq, detune: knockEngineData.detune, volume: knockEngineVolume, lockToKey: knockEngineData.lock_to_key }}
                  bpm={sessionBpm} keySignature={`${knockEngineData.root_note} ${knockEngineData.scale_type}`}
                  getContext={getBassContext} onChange={updateKnockBass} onClose={() => setBassEditorOpen(false)}
                  onStopArrangement={() => { if (isPlaying) togglePlayback(); }} onListen={() => togglePlayback()}
                  onGenerate={() => { const key = parseSessionKey(`${knockEngineData.root_note} ${knockEngineData.scale_type}`); return generatePattern(key.root,key.scaleType,knockEngineData.feel,knockEngineData.genre,Date.now(),[]); }}
                /></div>) : <>
                {/* Track Rows */}
                {tracks.map((track, index) => (
                  <TrackRow 
                    key={track.id} 
                    track={track} 
                    index={index}
                    onRemove={() => handleRemoveTrack(track, index)}
                    onVolumeChange={(vol) => setTrackVolume(track.id, vol)}
                    onMuteToggle={() => toggleTrackMute(track.id)}
                  />
                ))}
                
                {/* Empty Slots */}
                {tracks.length < MAX_TRACKS && (
                  <div className="border-2 border-dashed border-white/10 rounded-xl p-3 text-center text-muted-foreground text-sm">
                    + {MAX_TRACKS - tracks.length} slot{MAX_TRACKS - tracks.length > 1 ? 's' : ''} available
                  </div>
                )}
                
                <KeyLearning keySignature={tracks.find(t => t.instrumentGroup === 'Melodies')?.musicalKey} getContext={getBassContext} onStopArrangement={() => { if (isPlaying) togglePlayback(); }} portrait="/pizzul/writing.png" />
                {bassLoadError && <p role="alert">{bassLoadError}</p>}

                {/* Knock Engine Section */}
                <div className="mt-4 pt-4 border-t border-white/10">
                  {knockEngineAvailable() ? (
                    knockEngineActive ? (
                      <div className="rounded-xl bg-gradient-to-r from-emerald-500/20 to-emerald-500/20 border border-emerald-500/30 p-3" data-testid="knock-engine-layer">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-emerald-500/30 flex items-center justify-center">
                              <Zap className="w-4 h-4 text-emerald-400" />
                            </div>
                            <div>
                              <p className="font-semibold text-sm text-emerald-400">Knock Engine Bass</p>
                              <p className="text-xs text-muted-foreground">
                                {knockEngineData?.template_name} • {knockEngineData?.root_note}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={removeKnockEngine}
                            className="p-2 hover:bg-red-500/20 hover:text-red-400 rounded-lg transition-colors text-muted-foreground"
                            title="Remove"
                            data-testid="knock-remove-btn"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        
                        <div className="flex items-center gap-2 mb-3">
                          <button
                            onClick={toggleKnockEngineMute}
                            className={`p-1 rounded transition-colors ${
                              knockEngineMuted 
                                ? 'text-red-400 hover:text-red-300' 
                                : 'text-emerald-400/70 hover:text-emerald-400'
                            }`}
                            title={knockEngineMuted ? 'Unmute' : 'Mute'}
                            data-testid="knock-mute-btn"
                          >
                            {knockEngineMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                          </button>
                          <Slider
                            value={[knockEngineVolume * 100]}
                            onValueChange={([v]) => updateKnockEngineVolume(v / 100)}
                            max={100}
                            step={1}
                            className="flex-1"
                            disabled={knockEngineMuted}
                            data-testid="knock-volume-slider"
                          />
                          <span className={`text-xs w-8 text-right ${knockEngineMuted ? 'text-red-400' : 'text-muted-foreground'}`}>
                            {knockEngineMuted ? 'M' : `${Math.round(knockEngineVolume * 100)}%`}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setBassEditorOpen(true)}
                            disabled={knockEngineLoading}
                            className="flex-1 py-2 px-3 rounded-lg bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center gap-2 text-sm"
                            title="Edit bass notes and sound"
                            data-testid="knock-variation-btn"
                          >
                            <Shuffle className={`w-4 h-4 ${knockEngineLoading ? 'animate-spin' : ''}`} />
                            <span>Edit bass</span>
                          </button>
                          <button
                            onClick={() => setBassEditorOpen(true)}
                            disabled={knockEngineLoading}
                            className="flex-1 py-2 px-3 rounded-lg bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center gap-2 text-sm"
                            title="Choose a bass sound"
                            data-testid="knock-lock-groove-btn"
                          >
                            <Lock className="w-4 h-4" />
                            <span>{knockEngineData?.sound_name || 'Sub Sine'}</span>
                          </button>
                        </div>
                        
                        <button
                          onClick={toggleKnockEngineKickFollow}
                          className={`w-full mt-2 py-2 px-3 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm ${
                            knockEngineKickFollow 
                              ? 'bg-emerald-500/30 border border-emerald-500/50 text-emerald-400'
                              : 'bg-white/10 border border-white/10 text-muted-foreground'
                          }`}
                          title={knockEngineKickFollow && detectedKickPositions?.length > 0 
                            ? `Detected ${detectedKickPositions.length} kick positions` 
                            : 'Align bass with kick drum timing'}
                          data-testid="knock-kick-follow-btn"
                        >
                          <Music className="w-4 h-4" />
                          <span>
                            Kick Follow {knockEngineKickFollow ? 'ON' : 'OFF'}
                            {knockEngineKickFollow && detectedKickPositions?.length > 0 && (
                              <span className="ml-1 text-xs opacity-75">
                                ({detectedKickPositions.length} hits)
                              </span>
                            )}
                          </span>
                        </button>
                        
                        <button
                          onClick={async () => {
                            try {
                              toast.info('Rendering bassline...');
                              const result = await addKnockEngineToCrate();
                              if (result.success) {
                                toast.success('Rendered bass added to crate!');
                              } else {
                                toast.info(result.message);
                              }
                            } catch (e) {
                              toast.error('Failed to render bassline');
                            }
                          }}
                          className="w-full mt-2 py-2 px-3 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 transition-colors flex items-center justify-center gap-2 text-sm text-emerald-400"
                          data-testid="knock-add-to-crate-btn"
                        >
                          <ShoppingBag className="w-4 h-4" />
                          <span>Add to Crate</span>
                        </button>
                      </div>
                    ) : (
                      <div className="rounded-xl bg-gradient-to-r from-emerald-500/10 to-emerald-500/10 border border-emerald-500/30 p-3">
                        <div className="flex items-center gap-1 mb-3">
                          <span className="text-xs text-muted-foreground mr-2">Feel:</span>
                          {FEEL_OPTIONS.map(({ value, label }) => (
                            <button
                              key={value}
                              onClick={() => changeKnockEngineFeel(value)}
                              className={`px-2 py-1 text-xs rounded-full transition-all ${
                                knockEngineFeel === value
                                  ? 'bg-emerald-500 text-black font-semibold'
                                  : 'bg-white/10 hover:bg-white/20 text-muted-foreground'
                              }`}
                              data-testid={`knock-feel-select-${value}`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              await activateKnockEngine();
                              setBassEditorOpen(true);
                              toast.success('Knock Engine activated!');
                            } catch (e) {
                              toast.error(e.message || 'Failed to activate Knock Engine');
                            }
                          }}
                          disabled={knockEngineLoading}
                          className="w-full py-2 px-4 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/50 transition-all flex items-center justify-center gap-2 text-emerald-400 hover:text-emerald-300"
                          data-testid="knock-engine-activate-btn"
                        >
                          {knockEngineLoading ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Zap className="w-4 h-4" />
                          )}
                          <span className="font-semibold text-sm">Add Knock Engine Bass</span>
                        </button>
                      </div>
                    )
                  ) : (
                    <div className="text-center py-3 px-4 rounded-xl bg-muted/30 border border-white/5">
                      <div className="flex items-center justify-center gap-2 text-muted-foreground mb-1">
                        <Zap className="w-4 h-4" />
                        <span className="text-sm font-medium">Knock Engine</span>
                      </div>
                      <p className="text-xs text-muted-foreground/70">
                        Add melody + drums to build your beat first
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Beat Arranger Button */}
                <div className="mt-4 pt-4 border-t border-white/10">
                  <button
                    onClick={() => setConfirmSend(true)}
                    disabled={tracks.length === 0}
                    className={`w-full py-2.5 px-4 rounded-full transition-all flex items-center justify-center gap-2 text-sm font-semibold ${
                      tracks.length > 0
                        ? 'bg-primary text-black hover:bg-primary/90'
                        : 'bg-muted/50 text-muted-foreground cursor-not-allowed'
                    }`}
                    title={tracks.length === 0 ? 'Add loops to your preview first.' : 'Send these loops to Beat Arranger'}
                    data-testid="arrange-beat-arranger-btn"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>Send to Beat Arranger</span>
                  </button>
                </div>
                </>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dock Bar — always visible, unified now-playing indicator */}
      <div
        className={`border-t backdrop-blur-xl transition-colors duration-200 ${
          isAudioActive
            ? 'bg-primary/10 border-primary/40 shadow-[0_-4px_20px_rgba(var(--primary-rgb,0,255,200),0.15)]'
            : 'bg-card border-white/10'
        } ${justAdded ? 'ring-2 ring-primary/70' : ''}`}
        data-testid="preview-dock-bar"
      >
        <div className="max-w-3xl mx-auto min-h-16 px-4 py-2 flex items-center gap-3">
          {/* Play / Stop — only for preview (hidden when card-only with no tracks) */}
          {hasPreviewContent && (
            <Button
              data-testid="preview-play-btn"
              onClick={togglePlayback}
              disabled={tracks.length === 0}
              className={`rounded-full w-10 h-10 flex-shrink-0 ${
                isPlaying 
                  ? 'bg-red-500 hover:bg-red-600' 
                  : 'bg-primary hover:bg-primary/90'
              } text-black`}
            >
              {isPlaying ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
            </Button>
          )}

          {/* Clickable info area — unified label */}
          <button
            onClick={handleDockInfoClick}
            className="flex items-center gap-3 flex-1 min-w-0 hover:bg-white/5 rounded-lg px-2 py-1.5 transition-colors text-left"
            data-testid="preview-toggle-btn"
          >
            <Music className={`w-4 h-4 flex-shrink-0 ${isAudioActive ? 'text-primary' : 'text-muted-foreground'}`} />
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-semibold truncate ${isAudioActive ? 'text-primary' : 'text-foreground'}`} data-testid="dock-label">
                {hasPreviewContent ? `Your beat · ${sessionBpm || '—'} BPM` : (dockLabel || 'Preview')}
              </p>
              {hasPreviewContent && !isCardOwner ? (
                <div className="flex gap-1 mt-1 overflow-x-auto" data-testid="dock-track-dots">
                  {coach.slots.map((slot, index) => slot.type === 'track' ? <span key={`${slot.track.id}-${index}`} title={`${slot.track.title} (${slot.group})`} style={{ backgroundColor: GROUP_HEX[slot.group] }} className="shrink-0 w-5 h-2.5 rounded-full" /> : slot.type === 'core' ? <button key={slot.group} onClick={(event) => { event.stopPropagation(); window.dispatchEvent(new CustomEvent('sp-dock-select-group', { detail: slot.group })); }} style={{ borderColor: GROUP_HEX[slot.group], color: GROUP_HEX[slot.group] }} className={`shrink-0 h-6 px-2 rounded-full border text-xs font-bold ${slot.next ? 'border-2' : 'opacity-60'}`}>{slot.next ? `+ ${slot.group === 'Melodies' ? 'Melody' : slot.group === 'Textures' ? 'Texture' : slot.group}` : ''}</button> : <span key={`empty-${index}`} className="shrink-0 w-5 h-2.5 rounded-full bg-white/10" />)}
                </div>
              ) : dockSub && (
                <p className="text-xs text-muted-foreground truncate">{dockSub}</p>
              )}
            </div>
            {/* Open shows an X, because at that point the only thing the control
                does is close the panel, and a downward chevron read as "minimise
                to somewhere" rather than "close". Closed keeps the up-chevron,
                which is honest: it expands. */}
            {hasPreviewContent && (
              <div className="flex-shrink-0">
                {isOpen ? (
                  <X className="w-5 h-5 text-muted-foreground" aria-label="Close preview" />
                ) : (
                  <ChevronUp className="w-5 h-5 text-muted-foreground" aria-label="Open preview" />
                )}
              </div>
            )}
          </button>

          {/* Send from the bar itself once there is something worth arranging, so
              the flow is browse, add, listen, send without opening the panel. The
              same confirm dialog guards it. */}
          {hasPreviewContent && tracks.length >= 2 && !isOpen && (
            <Button
              size="sm"
              onClick={() => setConfirmSend(true)}
              className="rounded-full bg-primary text-black hover:bg-primary/90 flex-shrink-0"
              data-testid="dock-send-btn"
              title="Send these loops to Beat Arranger"
            >
              <ExternalLink className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Send to Beat Arranger</span>
            </Button>
          )}

          {/* Clear — only when preview has tracks */}
          {hasPreviewContent && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="text-muted-foreground hover:text-red-400 flex-shrink-0 ml-3 border-l border-white/10 rounded-none pl-3"
              data-testid="preview-clear-btn"
              title="Clear all loops"
              aria-label="Clear all loops"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}

          {/* Stop card — when card owns playback and no preview tracks */}
          {isCardOwner && !hasPreviewContent && (
            <Button
              variant="ghost"
              size="sm"
              onClick={stopCard}
              className="text-muted-foreground hover:text-red-400 flex-shrink-0"
              data-testid="card-stop-btn"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      <AlertDialog open={confirmSend} onOpenChange={setConfirmSend}>
        <AlertDialogContent className="bg-card border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle>Send to Beat Arranger?</AlertDialogTitle>
            <AlertDialogDescription>
              {tracks.length} {tracks.length === 1 ? 'loop' : 'loops'} will open in
              Beat Arranger in a new tab. Studio Playground stays open here, so you
              can come back and keep digging.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not yet</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-send-to-arranger"
              onClick={handleArrangeInBeatArranger}
            >
              Send it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Track Row Component
const TrackRow = ({ track, index, onRemove, onVolumeChange, onMuteToggle }) => {
  const { addToCrate, removeFromCrate, isInCrate } = usePreview();
  const inCrate = isInCrate(track.loopId);
  
  const handleToggleCrate = () => {
    if (inCrate) {
      removeFromCrate(track.loopId);
      toast.success('Removed from crate');
    } else {
      const loopData = {
        loop_id: track.loopId,
        title: track.title,
        bpm: track.bpm,
        preview_url: track.previewUrl,
        download_url: track.downloadUrl,
        instrument_group: track.instrumentGroup
      };
      const result = addToCrate(loopData);
      if (result.success) {
        toast.success('Added to crate');
      }
    }
  };
  
  // Colours come from utils/groupColors, never a local copy. This file used to
  // keep its own map that had Drums orange and Textures blue, so the same loop
  // was one colour on its card and a different colour in the track bar.
  return (
    <div 
      className={`relative overflow-hidden rounded-xl p-3 pl-4 border ${surfaceForGroup(track.instrumentGroup)} ${
        track.muted ? 'opacity-50' : ''
      }`}
      data-testid={`preview-track-${index}`}
      data-instrument-group={track.instrumentGroup || ''}
    >
      <span
        className={`absolute left-0 top-0 bottom-0 w-1.5 ${dotForGroup(track.instrumentGroup)}`}
        aria-hidden="true"
      />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-xs font-mono text-muted-foreground w-4">{index + 1}</span>
        
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{track.title}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`text-xs px-1.5 py-0.5 rounded bg-black/30 font-medium ${textForGroup(track.instrumentGroup)}`}>
              {track.instrumentGroup || 'Loop'}
            </span>
            <span className="text-xs text-muted-foreground">{track.bpm} BPM</span>
          </div>
          {track.musicalKey && track.instrumentGroup === 'Melodies' && (
            <p className="text-xs text-violet-400 mt-0.5">{track.musicalKey}</p>
          )}
        </div>
        
        <button
          onClick={handleToggleCrate}
          className={`p-2 rounded-lg transition-colors ${
            inCrate 
              ? 'bg-emerald-500/20 text-emerald-400 hover:bg-red-500/20 hover:text-red-400' 
              : 'hover:bg-emerald-500/20 hover:text-emerald-400 text-muted-foreground'
          }`}
          title={inCrate ? 'Remove from Crate' : 'Add to Crate'}
        >
          {inCrate ? <Check className="w-4 h-4" /> : <ShoppingBag className="w-4 h-4" />}
        </button>
        
        <button
          onClick={onMuteToggle}
          className={`p-2 rounded-lg transition-colors ${
            track.muted 
              ? 'bg-red-500/20 text-red-400' 
              : 'hover:bg-white/10 text-muted-foreground'
          }`}
        >
          {track.muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
        
        <button
          onClick={onRemove}
          className="p-2 hover:bg-red-500/20 hover:text-red-400 rounded-lg transition-colors text-muted-foreground"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      
      <div className="flex items-center gap-2 px-1">
        <Volume2 className="w-3 h-3 text-muted-foreground" />
        <Slider
          value={[track.volume * 100]}
          onValueChange={([val]) => onVolumeChange(val / 100)}
          max={100}
          step={1}
          className="flex-1"
        />
        <span className="text-xs text-muted-foreground w-8 text-right">
          {Math.round(track.volume * 100)}%
        </span>
      </div>
    </div>
  );
};

export default PreviewPanel;
